import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { apiFetch } from "../lib/api";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { can } from "@family/shared";
import type { FamilyMembersResponse } from "@family/shared";

interface TestCase {
  id: string;
  title: string;
  description: string | null;
  assigneeId: string | null;
  assigneeName: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  commentCount?: number;
}

interface TestComment {
  id: string;
  body: string;
  authorId: string;
  authorName: string | null;
  createdAt: string;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  not_started: { label: "Not Started", color: "var(--c-text-subtle)" },
  in_progress: { label: "In Progress", color: "var(--c-accent)" },
  completed: { label: "Completed", color: "var(--c-success)" },
};

export function TestingPage() {
  const meQuery = useMe();
  const primary = resolveMembership(meQuery.data);
  const slug = primary?.familySlug;
  const isAdmin = primary ? can(primary.role, "members:manage") : false;
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<"cases" | "members">("cases");
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newAssignee, setNewAssignee] = useState("");
  const [expandedCase, setExpandedCase] = useState<string | null>(null);

  const casesQuery = useQuery<{ cases: TestCase[] }>({
    queryKey: ["test-cases", slug],
    queryFn: () => apiFetch(`/families/${slug}/testing/cases`),
    enabled: Boolean(slug),
  });

  const membersQuery = useQuery<{ members: { userId: string; displayName: string | null; email: string | null }[] }>({
    queryKey: ["test-members", slug],
    queryFn: () => apiFetch(`/families/${slug}/testing/members`),
    enabled: Boolean(slug) && isAdmin,
  });

  const familyMembersQuery = useQuery<FamilyMembersResponse>({
    queryKey: ["members", slug],
    queryFn: () => apiFetch<FamilyMembersResponse>(`/families/${slug}/members`),
    enabled: Boolean(slug) && isAdmin,
  });

  const createCase = useMutation({
    mutationFn: (body: any) =>
      apiFetch(`/families/${slug}/testing/cases`, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => {
      setShowCreate(false);
      setNewTitle("");
      setNewDesc("");
      setNewAssignee("");
      void queryClient.invalidateQueries({ queryKey: ["test-cases", slug] });
    },
  });

  const updateStatus = useMutation({
    mutationFn: ({ caseId, status }: { caseId: string; status: string }) =>
      apiFetch(`/families/${slug}/testing/cases/${caseId}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["test-cases", slug] }),
  });

  const deleteCase = useMutation({
    mutationFn: (caseId: string) =>
      apiFetch(`/families/${slug}/testing/cases/${caseId}`, { method: "DELETE" }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["test-cases", slug] }),
  });

  const addTestMember = useMutation({
    mutationFn: (userId: string) =>
      apiFetch(`/families/${slug}/testing/members`, { method: "POST", body: JSON.stringify({ userId }) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["test-members", slug] }),
  });

  const removeTestMember = useMutation({
    mutationFn: (userId: string) =>
      apiFetch(`/families/${slug}/testing/members/${userId}`, { method: "DELETE" }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["test-members", slug] }),
  });

  if (!primary || !slug) {
    return <><AppHeader /><main className="page"><p className="text-muted">Loading…</p></main></>;
  }

  const cases = casesQuery.data?.cases ?? [];
  const testMembers = membersQuery.data?.members ?? [];
  const allFamilyMembers = familyMembersQuery.data?.members ?? [];
  const testMemberIds = new Set(testMembers.map((m) => m.userId));

  return (
    <>
      <AppHeader />
      <main className="page stack-lg">
        <h1>Testing</h1>

        {isAdmin && (
          <nav style={{ display: "flex", gap: "var(--s-2)", borderBottom: "1px solid var(--c-border)", marginBottom: "var(--s-3)" }}>
            {(["cases", "members"] as const).map((t) => (
              <button key={t} type="button" onClick={() => setTab(t)}
                style={{
                  background: "transparent", border: "none", borderBottom: tab === t ? "2px solid var(--c-blue)" : "2px solid transparent",
                  color: tab === t ? "var(--c-blue)" : "var(--c-text-muted)",
                  fontWeight: 700, padding: "8px 14px", minHeight: 40, borderRadius: 0, textTransform: "capitalize", cursor: "pointer",
                }}>
                {t === "cases" ? "Test Cases" : "Test Group Members"}
              </button>
            ))}
          </nav>
        )}

        {tab === "cases" && (
          <section className="stack">
            {isAdmin && !showCreate && (
              <button onClick={() => setShowCreate(true)} style={{ alignSelf: "flex-start" }}>
                + New test case
              </button>
            )}

            {showCreate && (
              <form onSubmit={(e) => { e.preventDefault(); if (newTitle.trim()) createCase.mutate({ title: newTitle.trim(), description: newDesc.trim() || undefined, assigneeId: newAssignee || undefined }); }}
                className="card stack">
                <h3 style={{ margin: 0, fontSize: "1rem" }}>New test case</h3>
                <div>
                  <label htmlFor="tc-title">Title *</label>
                  <input id="tc-title" type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} required />
                </div>
                <div>
                  <label htmlFor="tc-desc">Description</label>
                  <textarea id="tc-desc" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} rows={3}
                    style={{ width: "100%", background: "var(--c-bg)", color: "var(--c-text)", border: "1px solid var(--c-border-strong)", borderRadius: 4, padding: "14px 16px", fontSize: "1rem", fontFamily: "inherit", resize: "vertical" }} />
                </div>
                <div>
                  <label htmlFor="tc-assignee">Assign to</label>
                  <select id="tc-assignee" value={newAssignee} onChange={(e) => setNewAssignee(e.target.value)}
                    style={{ width: "100%", padding: "10px 12px", fontSize: "1rem", border: "1px solid var(--c-border-strong)", borderRadius: 4, background: "var(--c-bg)", color: "var(--c-text)" }}>
                    <option value="">— Unassigned —</option>
                    {testMembers.map((m) => (
                      <option key={m.userId} value={m.userId}>{m.displayName ?? m.email ?? m.userId.slice(0, 8)}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: "flex", gap: "var(--s-3)" }}>
                  <button type="submit" disabled={createCase.isPending || !newTitle.trim()}>
                    {createCase.isPending ? "Creating…" : "Create"}
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                </div>
              </form>
            )}

            {casesQuery.isLoading && <p className="text-muted">Loading…</p>}

            {cases.map((tc) => {
              const st = STATUS_LABELS[tc.status] ?? STATUS_LABELS.not_started!;
              return (
                <div key={tc.id} className="card">
                  <div className="row-between test-case-row">
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <strong>{tc.title}</strong>
                        <span style={{ fontSize: "0.75rem", fontWeight: 700, color: st.color, textTransform: "uppercase" }}>
                          {st.label}
                        </span>
                      </div>
                      {tc.description && <p className="text-muted" style={{ fontSize: "0.85rem", marginTop: 4 }}>{tc.description}</p>}
                      <div className="text-subtle" style={{ fontSize: "0.8rem", marginTop: 4 }}>
                        {tc.assigneeName ? `Assigned to ${tc.assigneeName}` : "Unassigned"}
                      </div>
                    </div>
                    <div className="test-case-controls" style={{ display: "flex", gap: "var(--s-2)", alignItems: "center" }}>
                      <select
                        value={tc.status}
                        onChange={(e) => updateStatus.mutate({ caseId: tc.id, status: e.target.value })}
                        style={{ padding: "4px 8px", fontSize: "0.8rem", borderRadius: 4, border: "1px solid var(--c-border-strong)", background: "var(--c-bg)" }}
                      >
                        <option value="not_started">Not Started</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </select>
                      <button type="button" className="btn-tertiary"
                        onClick={() => setExpandedCase(expandedCase === tc.id ? null : tc.id)}
                        style={{
                          fontSize: "0.8rem",
                          padding: "4px 10px",
                          minHeight: 28,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                        aria-label={
                          tc.commentCount
                            ? `${tc.commentCount} comment${tc.commentCount === 1 ? "" : "s"}`
                            : "Add comment"
                        }
                      >
                        💬{tc.commentCount ? <span>{tc.commentCount}</span> : null}
                      </button>
                      {isAdmin && (
                        <button type="button" className="btn-tertiary"
                          onClick={() => { if (window.confirm("Delete this test case?")) deleteCase.mutate(tc.id); }}
                          style={{ fontSize: "0.8rem", padding: "4px 8px", minHeight: 28, color: "var(--c-danger)" }}>
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                  {expandedCase === tc.id && <TestCaseComments familySlug={slug} caseId={tc.id} />}
                </div>
              );
            })}
          </section>
        )}

        {tab === "members" && isAdmin && (
          <section className="stack">
            <p className="text-muted">Members in the test group can see the Testing page and update test case statuses.</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: "var(--s-3)" }}>
              {testMembers.map((m) => (
                <span key={m.userId} style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "4px 10px", borderRadius: "var(--r-pill)",
                  background: "var(--c-bg-alt)", border: "1px solid var(--c-border)", fontSize: "0.85rem",
                }}>
                  {m.displayName ?? m.email ?? m.userId.slice(0, 8)}
                  <button type="button" onClick={() => removeTestMember.mutate(m.userId)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text-subtle)", fontSize: "0.9rem", padding: "0 2px", minHeight: 0 }}>
                    ✕
                  </button>
                </span>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <select id="add-test-member"
                style={{ flex: 1, padding: "6px 10px", fontSize: "0.85rem", border: "1px solid var(--c-border-strong)", borderRadius: 6, background: "var(--c-bg)", color: "var(--c-text)" }}>
                <option value="">Add member…</option>
                {allFamilyMembers.filter((m) => !testMemberIds.has(m.userId)).map((m) => (
                  <option key={m.userId} value={m.userId}>{m.displayName ?? m.email ?? m.userId.slice(0, 8)}</option>
                ))}
              </select>
              <button type="button" onClick={() => {
                const sel = (document.getElementById("add-test-member") as HTMLSelectElement).value;
                if (sel) addTestMember.mutate(sel);
              }} style={{ padding: "6px 14px", fontSize: "0.85rem", borderRadius: 6, minHeight: 32 }}>
                Add
              </button>
            </div>
          </section>
        )}
      </main>
    </>
  );
}

function TestCaseComments({ familySlug, caseId }: { familySlug: string; caseId: string }) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState("");

  const query = useQuery<{ comments: TestComment[] }>({
    queryKey: ["test-comments", caseId],
    queryFn: () => apiFetch(`/families/${familySlug}/testing/cases/${caseId}/comments`),
  });

  const add = useMutation({
    mutationFn: (text: string) =>
      apiFetch(`/families/${familySlug}/testing/cases/${caseId}/comments`, {
        method: "POST", body: JSON.stringify({ body: text }),
      }),
    onSuccess: () => {
      setBody("");
      void queryClient.invalidateQueries({ queryKey: ["test-comments", caseId] });
      void queryClient.invalidateQueries({ queryKey: ["test-cases", familySlug] });
    },
  });

  const comments = query.data?.comments ?? [];

  return (
    <div style={{ marginTop: "var(--s-3)", paddingTop: "var(--s-3)", borderTop: "1px solid var(--c-border)" }}>
      {comments.map((c) => (
        <div key={c.id} style={{ marginBottom: 8 }}>
          <span style={{ fontWeight: 700, fontSize: "0.85rem" }}>{c.authorName ?? "Unknown"}</span>
          <span className="text-subtle" style={{ fontSize: "0.75rem", marginLeft: 8 }}>
            {new Date(c.createdAt).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" })}
          </span>
          <p style={{ margin: "2px 0 0", fontSize: "0.9rem" }}>{c.body}</p>
        </div>
      ))}
      <form onSubmit={(e) => { e.preventDefault(); if (body.trim()) add.mutate(body.trim()); }}
        style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <input type="text" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add feedback…"
          style={{ flex: 1, padding: "6px 10px", fontSize: "0.85rem", border: "1px solid var(--c-border-strong)", borderRadius: 6, background: "var(--c-bg)", color: "var(--c-text)" }} />
        <button type="submit" disabled={add.isPending || !body.trim()}
          style={{ padding: "6px 14px", fontSize: "0.85rem", borderRadius: 6, minHeight: 32 }}>
          Post
        </button>
      </form>
    </div>
  );
}
